import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';

const GROUP_LABELS = {
  driver: 'Drivers', sales: 'Sales', dispatcher: 'Dispatchers',
  admin: 'Admin', maintenance: 'Maintenance',
};

const GROUP_ICONS = {
  driver: '\u{1F69A}', sales: '\u{1F4B0}', dispatcher: '\u{1F4DE}',
  admin: '\u{1F6E1}', maintenance: '\u{1F527}',
};

const GROUP_COLORS = {
  driver: ['#3b82f6', '#1d4ed8'],
  sales: ['#10b981', '#059669'],
  dispatcher: ['#f59e0b', '#d97706'],
  admin: ['#8b5cf6', '#6d28d9'],
  maintenance: ['#6b7280', '#4b5563'],
};

// Client-side mirror of server/utils/teamHierarchy.js slugifyTeamName
function slugifyTeam(teamName) {
  if (!teamName) return '';
  const s = String(teamName).trim();
  if (s === 'Driver Operations') return 'driver';
  return s.replace(/\s+(Drivers|Operations)\s*$/i, '')
    .toLowerCase()
    .replace(/\s+/g, '_');
}

// Title-case fallback for slugs with no team backing ("fort_worth" → "Fort Worth")
function titleCaseSlug(slug) {
  return String(slug || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export default function BoardIndexPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [boards, setBoards] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [boardData, teamData] = await Promise.all([
        api.getLeaderboardBoardSummary(),
        api.getTeams().catch(() => []),
      ]);
      setBoards(Array.isArray(boardData) ? boardData : []);
      setTeams(Array.isArray(teamData) ? teamData : []);
    } catch {
      setBoards([]);
      setTeams([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Build slug → { label, parent_slug } map from the teams tree. Sub-teams
  // inherit their parent's color + icon so Dallas/Denton/Fort Worth/Day all
  // look like variants of the Drivers board rather than neutral gray.
  const teamBySlug = useMemo(() => {
    const byId = {};
    for (const t of teams) byId[t.id] = t;
    const map = {};
    for (const t of teams) {
      const slug = slugifyTeam(t.name);
      if (!slug) continue;
      const parentSlug = t.parent_team_id && byId[t.parent_team_id]
        ? slugifyTeam(byId[t.parent_team_id].name)
        : null;
      map[slug] = { label: t.name, parent_slug: parentSlug };
    }
    return map;
  }, [teams]);

  if (loading) return <div className="admin-page"><div className="loading-spinner" /></div>;

  // Soft, on-brand backdrop: subtle gold + navy radial glows on a light
  // canvas. Adds depth without competing with the colored board cards.
  const pageStyle = {
    minHeight: '100%',
    background: [
      'radial-gradient(1100px 600px at -10% -20%, rgba(212,168,67,0.10), transparent 60%)',
      'radial-gradient(900px 600px at 110% 120%, rgba(29,41,61,0.08), transparent 60%)',
      'linear-gradient(180deg, #fbfcfe 0%, #f4f6fb 100%)',
    ].join(', '),
  };

  if (boards.length === 0) {
    return (
      <div className="admin-page" style={pageStyle}>
        <h1 style={{ fontSize: 26, fontWeight: 900, marginBottom: 8, letterSpacing: '-0.5px' }}>Leaderboards</h1>
        <div style={{ textAlign: 'center', padding: '80px 20px', color: '#94a3b8' }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>{'\u{1F3C6}'}</div>
          <p style={{ fontSize: 17, fontWeight: 700, color: '#475569' }}>No leaderboards configured yet.</p>
          {can('manage_admin') && (
            <p style={{ fontSize: 14, color: '#94a3b8', marginTop: 8 }}>
              Create KPIs and assign them to groups in{' '}
              <a href="/admin-mod/leaderboard-kpis" style={{ color: '#d4a843', fontWeight: 600 }}>KPI Setup</a>.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page" style={pageStyle}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 28, fontWeight: 900, marginBottom: 6, letterSpacing: '-0.5px', color: '#0f172a' }}>
          Leaderboards
        </h1>
        <p style={{ color: '#64748b', fontSize: 13, margin: 0 }}>
          {boards.length} active board{boards.length !== 1 ? 's' : ''}
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
        {boards.map(board => {
          const teamInfo = teamBySlug[board.group_name];
          // Color/icon key: sub-team → parent's, else the slug itself
          const colorKey = teamInfo?.parent_slug || board.group_name;
          const colors = GROUP_COLORS[colorKey] || GROUP_COLORS[board.group_name] || ['#64748b', '#475569'];
          const icon = GROUP_ICONS[colorKey] || GROUP_ICONS[board.group_name] || '\u{1F4CA}';
          // Label: custom → team name → legacy hardcoded → title-cased slug
          const label = board.custom_label
            || teamInfo?.label
            || GROUP_LABELS[board.group_name]
            || titleCaseSlug(board.group_name);
          // Sub-tag shown beneath label for sub-teams (e.g. "of Driver Operations")
          const parentLabel = teamInfo?.parent_slug
            ? (teamBySlug[teamInfo.parent_slug]?.label || GROUP_LABELS[teamInfo.parent_slug])
            : null;

          return (
            <div
              key={board.group_name}
              onClick={() => navigate(`/leaderboard/${board.group_name}`)}
              style={{
                background: `linear-gradient(135deg, ${colors[0]}, ${colors[1]})`,
                borderRadius: 16, padding: '28px 24px', cursor: 'pointer',
                transition: 'transform 0.15s, box-shadow 0.15s',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                position: 'relative', overflow: 'hidden',
                minHeight: 160,
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 10px 32px rgba(0,0,0,0.2)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)'; }}
            >
              {/* Deco circles */}
              <div style={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
              <div style={{ position: 'absolute', bottom: -10, left: -10, width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />

              <div style={{ fontSize: 36, marginBottom: 12 }}>{icon}</div>
              <div style={{ fontSize: 20, fontWeight: 900, color: '#fff', marginBottom: 4, letterSpacing: '-0.3px' }}>{label}</div>
              {parentLabel && (
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                  {parentLabel}
                </div>
              )}
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', fontWeight: 600, marginTop: parentLabel ? 0 : 6 }}>
                {board.member_count} member{board.member_count !== 1 ? 's' : ''} &middot; {board.kpi_count} KPI{board.kpi_count !== 1 ? 's' : ''}
              </div>

              {/* Leader preview */}
              {board.leader_name && (
                <div style={{
                  marginTop: 16, display: 'flex', alignItems: 'center', gap: 8,
                  background: 'rgba(255,255,255,0.15)', borderRadius: 10, padding: '8px 12px',
                  backdropFilter: 'blur(4px)',
                }}>
                  <span style={{ fontSize: 16 }}>{'\u{1F451}'}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>{board.leader_name}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
